/**
 * Matrix-protocol chat service for nest.thephenom.app.
 *
 * Uses matrix-js-sdk, the official client library for the Matrix protocol.
 * The homeserver this currently talks to is our Synapse 1.105.0 instance
 * at chat.thephenom.app, but everything in this file is protocol-level
 * (rooms, events, redactions, power-levels), not Synapse-specific. If we
 * ever swap Synapse for Dendrite or Conduit, this file does not change.
 *
 * Drop-in replacement for the previous hasura-chat.ts. Exposes the same
 * function surface (fetchRooms, fetchMessages, sendMessage, ...) so the
 * use-chat / use-rooms hooks consume it unchanged.
 *
 * Authentication: the Cognito ID token is passed directly as the Matrix
 * access token. Our Synapse is configured with `org.matrix.login.jwt` (so
 * it validates the Cognito JWT against the configured Cognito JWKS) and an
 * `oidc-cognito` identity provider for `m.login.sso`. Both routes terminate
 * at the same Matrix access-token credential the SDK uses for every
 * subsequent request. The Cognito PostConfirmation Lambda
 * (phenom-{env}-chat-user-provisioner) already creates the corresponding
 * Matrix user on signup.
 *
 * Pattern mirrored from the mobile client at:
 *   PhenomApp/app/services/chat/matrixChatService.ts
 *
 * Mapping notes (chat-types.ts shape -> Matrix concept):
 *   - Hasura uuid room ids are replaced by Matrix room ids (!abc:server).
 *   - is_deleted maps to Matrix redactions (event.isRedacted()).
 *   - role maps from Matrix power levels: >=100 admin, >=50 support, else user.
 *   - mute has no native Matrix equivalent; we negative-power-level the user.
 *   - Cognito sub claim is decoded client-side to construct the Matrix user id.
 */

import {
  ClientEvent,
  createClient,
  EventType,
  MatrixClient,
  MatrixEvent,
  MsgType,
  Room,
  RoomEvent,
} from 'matrix-js-sdk'
import type { ChatRoom, ChatMessage, ChatMember } from '../data/chat-types'

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const DEFAULT_HOMESERVER =
  (import.meta.env.VITE_MATRIX_HOMESERVER_URL as string | undefined) ??
  (import.meta.env.VITE_CHAT_SERVER_URL as string | undefined) ??
  'https://chat.thephenom.app'  // pool-aligned post nest-pool migration (2026-05-24)

/** Chat server options exposed by the login UI's server-picker. */
export const CHAT_SERVERS = [
  { label: 'Staging', url: 'https://chat-staging.thephenom.app' },
  { label: 'Production', url: 'https://chat.thephenom.app' },
] as const

let _activeHomeserver = DEFAULT_HOMESERVER

export function setChatServer(url: string) {
  _activeHomeserver = url
  _adminSession = null
  if (_client) {
    _client.stopClient()
    _client = null
    _readyPromise = null
    _currentTokenTail = null
  }
}

export function getChatServer(): string {
  return _activeHomeserver
}

// ---------------------------------------------------------------------------
// Client lifecycle (singleton, reused across hook callers)
// ---------------------------------------------------------------------------

let _client: MatrixClient | null = null
let _readyPromise: Promise<MatrixClient> | null = null
let _currentTokenTail: string | null = null

/** A REST-only Matrix session for the admin surface — see getMatrixAdminSession. */
interface MatrixAdminSession {
  accessToken: string
  userId: string
  homeserver: string
}

// Cached admin session, keyed on (homeserver, token tail) so a server switch or
// token rotation forces a fresh login. Deliberately separate from the chat
// client singleton: the admin panel needs a token, not a synced client.
let _adminSession: { key: string; promise: Promise<MatrixAdminSession> } | null =
  null

function tokenTail(token: string): string {
  // Last 16 chars is enough to detect token rotation without storing the full token.
  return token.slice(-16)
}

function decodeCognitoSub(token: string): string {
  const parts = token.split('.')
  if (parts.length < 2) throw new Error('Malformed Cognito JWT')
  const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
  if (!payload.sub) throw new Error('Cognito JWT has no sub claim')
  return payload.sub as string
}

function matrixUserIdFor(sub: string, homeserver: string): string {
  const domain = new URL(homeserver).hostname
  return `@${sub}:${domain}`
}

// Pick the best display name from a Cognito ID token. Order:
// 1. `name` (e.g. "Matthew Stevens" — the full-name attribute)
// 2. `preferred_username` (e.g. "matt")
// 3. email localpart (e.g. "matt" from matt@sanmarcsoft.com)
// Synapse's org.matrix.login.jwt flow does not propagate any of these into the
// Matrix profile on its own (unlike m.login.sso which honours
// oidc_providers.user_mapping_provider.display_name_template). We sync them
// client-side after login so chat shows readable names instead of sub UUIDs.
function deriveDisplayName(cognitoIdToken: string): string | null {
  try {
    const parts = cognitoIdToken.split('.')
    if (parts.length < 2) return null
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
    const name = (payload.name as string | undefined)?.trim()
    if (name) return name
    const pref = (payload.preferred_username as string | undefined)?.trim()
    if (pref) return pref
    const email = (payload.email as string | undefined)?.trim()
    if (email) {
      const local = email.split('@')[0]
      if (local) return local
    }
    return null
  } catch {
    return null
  }
}

// Every JWT-derivable name candidate for this token. Used to decide whether an
// EXISTING Matrix displayname is one of our own machine-derived fallbacks (and
// therefore safe to replace with a better profile-sourced name) as opposed to a
// human customisation, which is always kept. Federated (Apple/Google) tokens
// often carry no name/preferred_username, so the derived fallback degrades to
// an email localpart — for Apple, an unreadable private-relay string.
function jwtNameCandidates(cognitoIdToken: string): string[] {
  try {
    const parts = cognitoIdToken.split('.')
    if (parts.length < 2) return []
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
    const out: string[] = []
    for (const v of [payload.name, payload.preferred_username, payload.email]) {
      const s = (v as string | undefined)?.trim()
      if (s) out.push(s)
    }
    const local = (payload.email as string | undefined)?.trim()?.split('@')[0]
    if (local) out.push(local)
    return out
  } catch {
    return []
  }
}

// Idempotently set the Matrix displayname for the just-logged-in user. Only
// overrides when the current displayname is missing, equal to the localpart
// (Synapse's default for JWT-created users — the raw Cognito sub UUID), or one
// of our own machine-derived fallbacks (`replaceable` — see jwtNameCandidates),
// so a bad relay-email name self-heals once a better name is known. If a user
// has manually customised their name, we leave it alone.
async function ensureMatrixDisplayName(
  homeserver: string,
  accessToken: string,
  matrixUserId: string,
  desiredName: string | null,
  replaceable: readonly string[] = [],
): Promise<void> {
  if (!desiredName) return
  try {
    const enc = encodeURIComponent(matrixUserId)
    const localpart = matrixUserId.slice(1).split(':')[0]
    const cur = await fetch(
      `${homeserver}/_matrix/client/v3/profile/${enc}/displayname`,
      { method: 'GET' },
    )
    if (cur.ok) {
      const body = (await cur.json()) as { displayname?: string }
      const existing = body.displayname?.trim()
      if (existing === desiredName) return
      if (existing && existing !== localpart && !replaceable.includes(existing))
        return
    }
    await fetch(`${homeserver}/_matrix/client/v3/profile/${enc}/displayname`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ displayname: desiredName }),
    })
  } catch (e) {
    console.warn('[matrix-chat] displayname sync failed (non-fatal):', e)
  }
}

// The principal's profile-sourced display name (users row via /api/users/me),
// registered by the UI when the profile loads. Preferred over JWT-derived
// names — federated (Apple/Google) ID tokens often carry no readable name.
let _preferredDisplayName: string | null = null
let _lastIdToken: string | null = null

/**
 * Register the profile-sourced display name for the logged-in user. If a
 * Matrix session is already live, heal its displayname immediately (replacing
 * only machine-derived fallbacks, never a human customisation); otherwise the
 * name is used at the next login's displayname sync.
 */
export function registerPreferredDisplayName(name: string | null): void {
  const clean = name?.trim() || null
  if (clean === _preferredDisplayName) return
  _preferredDisplayName = clean
  if (clean && _client && _lastIdToken) {
    const accessToken = _client.getAccessToken()
    const userId = _client.getUserId()
    if (accessToken && userId) {
      void ensureMatrixDisplayName(
        _activeHomeserver,
        accessToken,
        userId,
        clean,
        jwtNameCandidates(_lastIdToken),
      )
    }
  }
}

interface JwtLoginResponse {
  user_id: string
  access_token: string
  home_server?: string
  device_id?: string
}

/**
 * Exchange the Cognito ID token for a Matrix access token via the homeserver's
 * org.matrix.login.jwt endpoint. Synapse's jwt_config validates the Cognito JWT
 * (audience must include our Cognito client id; aud-validation lives in
 * homeserver.yaml jwt_config.audiences). On first login for a sub, Synapse
 * auto-creates the Matrix account; no separate provisioning Lambda is needed.
 *
 * The returned access_token (syt_...) is a real Matrix access token. The
 * Cognito ID token is NOT accepted as a Matrix access token on its own; we
 * must do this exchange before any other Matrix-protocol call.
 */
async function matrixJwtLogin(
  homeserver: string,
  cognitoIdToken: string,
): Promise<JwtLoginResponse> {
  const res = await fetch(`${homeserver}/_matrix/client/v3/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: 'org.matrix.login.jwt', token: cognitoIdToken }),
  })
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as {
      errcode?: string
      error?: string
    }
    throw new Error(
      `Matrix login failed: ${err.errcode ?? res.status} ${err.error ?? ''}`,
    )
  }
  return (await res.json()) as JwtLoginResponse
}

export async function ensureClient(token: string): Promise<MatrixClient> {
  const tail = tokenTail(token)

  // Reuse any in-flight OR settled handshake for the same token. _readyPromise
  // is assigned synchronously below — before the first await — so the several
  // callers that hit the service at once on a cold page load (useChat messages,
  // useChat members, subscribeToMessages, useRooms) all share ONE login. Before
  // this guard keyed on the in-flight promise, each caller blew past the old
  // `_client && _readyPromise` check (both still null) and started its own
  // login → N devices and N racing MatrixClients that tore each other down via
  // stopClient(), 500ing the first room-select until a refresh warmed the
  // singleton (issue #339).
  if (_readyPromise && _currentTokenTail === tail) {
    return _readyPromise
  }

  // Token rotated (or first ever call): tear down any stale client.
  if (_client) {
    _client.stopClient()
    _client = null
  }

  _currentTokenTail = tail

  _readyPromise = (async () => {
    // Exchange Cognito ID token for a real Matrix access token. The user_id
    // returned by Synapse is authoritative — it uses each Synapse instance's
    // configured server_name, which equals its public hostname post-cutover
    // (chat.thephenom.app for prod, chat-staging.thephenom.app for staging).
    const login = await matrixJwtLogin(_activeHomeserver, token)

    const client = createClient({
      baseUrl: _activeHomeserver,
      accessToken: login.access_token,
      userId: login.user_id,
      deviceId: login.device_id,
    })
    _client = client
    _lastIdToken = token

    // Sync displayname into the Matrix profile (best-effort, does not block
    // initial sync). Prefer the profile-sourced name registered by the UI;
    // fall back to Cognito claims. Machine-derived fallbacks are marked
    // replaceable so a later, better name can heal them.
    void ensureMatrixDisplayName(
      _activeHomeserver,
      login.access_token,
      login.user_id,
      _preferredDisplayName ?? deriveDisplayName(token),
      jwtNameCandidates(token),
    )

    await new Promise<void>((resolve, reject) => {
      client.once(ClientEvent.Sync, (state) => {
        if (state === 'PREPARED') resolve()
        else reject(new Error(`Unexpected initial sync state: ${state}`))
      })
      client.startClient({ initialSyncLimit: 50 })
    })

    return client
  })().catch((err) => {
    // A failed handshake must not poison the singleton: clear it so the next
    // caller retries a fresh login rather than re-awaiting a rejected promise.
    if (_currentTokenTail === tail) {
      _readyPromise = null
      _currentTokenTail = null
    }
    if (_client) {
      try {
        _client.stopClient()
      } catch {
        // teardown is best-effort
      }
      _client = null
    }
    throw err
  })

  return _readyPromise
}

export function stopChatClient() {
  _adminSession = null
  if (_client) {
    _client.stopClient()
    _client = null
    _readyPromise = null
    _currentTokenTail = null
  }
}

/**
 * Full client-side teardown for a Matrix session, separate from a soft
 * sign-out. Use when the persisted client state was minted under a different
 * Cognito pool / issuer than the homeserver now validates (e.g. after the
 * nest pool cutover in PR #299), so reusing it produces "Chat authentication
 * failed". Drops the in-memory client, every matrix-js-sdk IndexedDB
 * database, every mx_/matrix_ key in local + session storage, and any
 * service-worker cache whose name contains matrix or chat. Each step is
 * isolated so a single failure cannot block the rest, and the function
 * never throws so the caller can rely on it before signOut().
 */
export async function resetChatStorage(): Promise<void> {
  try {
    stopChatClient()
  } catch {
    // in-memory teardown is best-effort
  }

  try {
    await wipeMatrixIndexedDb()
  } catch {
    // IndexedDB may be unavailable (private mode, blocked storage)
  }

  try {
    wipeMatrixWebStorage(
      typeof localStorage !== 'undefined' ? localStorage : null,
    )
  } catch {
    // localStorage may throw in restricted contexts
  }

  try {
    wipeMatrixWebStorage(
      typeof sessionStorage !== 'undefined' ? sessionStorage : null,
    )
  } catch {
    // sessionStorage may throw in restricted contexts
  }

  try {
    await wipeMatrixCaches()
  } catch {
    // Cache API absent on older browsers
  }
}

/**
 * Chat-scoped recovery: tear down persisted Matrix state then lazy-init a
 * fresh client against the current Cognito session. Keeps the user signed
 * into nest. Re-throws if the fresh handoff fails so the caller can render
 * the error rather than presenting a silent success.
 */
export async function reconnectChatClient(cognitoToken: string): Promise<{
  userId: string
  homeserver: string
  accessToken: string
}> {
  await resetChatStorage()
  return getMatrixSession(cognitoToken)
}

async function wipeMatrixIndexedDb(): Promise<void> {
  if (typeof indexedDB === 'undefined') return

  const namesToDrop: string[] = []
  const matches = (name: string): boolean =>
    name.startsWith('matrix-js-sdk') || name.startsWith('crypto.matrix.org')

  // indexedDB.databases() is Safari 16+ and all Chromium; the Safari < 16
  // fallback is a hardcoded list of names matrix-js-sdk has historically used.
  if ('databases' in indexedDB) {
    const infos = (await (
      indexedDB as IDBFactory & {
        databases: () => Promise<IDBDatabaseInfo[]>
      }
    ).databases()) as IDBDatabaseInfo[]
    for (const info of infos) {
      if (info.name && matches(info.name)) namesToDrop.push(info.name)
    }
  } else {
    namesToDrop.push(
      'matrix-js-sdk:riot-web-sync',
      'matrix-js-sdk:crypto',
      'matrix-js-sdk-sync',
    )
  }

  for (const name of namesToDrop) {
    await deleteIndexedDbDatabase(name)
  }
}

async function deleteIndexedDbDatabase(name: string): Promise<void> {
  let request: IDBOpenDBRequest
  try {
    request = indexedDB.deleteDatabase(name)
  } catch {
    return
  }
  await new Promise<void>((resolve) => {
    request.onsuccess = () => resolve()
    request.onerror = () => resolve()
    // onblocked fires when another tab still holds the db open. We resolve
    // anyway so the caller can proceed with sign-out; the stale db is
    // harmless once the access token is gone.
    request.onblocked = () => resolve()
  })
}

function wipeMatrixWebStorage(store: Storage | null): void {
  if (!store) return
  const pattern = /^mx_|^matrix_/
  const doomed: string[] = []
  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i)
    if (key && pattern.test(key)) doomed.push(key)
  }
  for (const key of doomed) store.removeItem(key)
}

async function wipeMatrixCaches(): Promise<void> {
  if (typeof caches === 'undefined') return
  const keys = await caches.keys()
  for (const name of keys) {
    const lower = name.toLowerCase()
    if (lower.includes('matrix') || lower.includes('chat')) {
      await caches.delete(name)
    }
  }
}

/**
 * Return the Matrix access token + user id + homeserver for the current
 * session. Used by features that need to call Matrix or Synapse Admin APIs
 * directly (the matrix-js-sdk does not cover the /_synapse/admin/* surface).
 */
export async function getMatrixSession(
  cognitoToken: string,
): Promise<{ accessToken: string; userId: string; homeserver: string }> {
  const client = await ensureClient(cognitoToken)
  const accessToken = client.getAccessToken()
  const userId = client.getUserId()
  if (!accessToken) throw new Error('Matrix access token not available')
  if (!userId) throw new Error('Matrix user id not available')
  return { accessToken, userId, homeserver: _activeHomeserver }
}

/**
 * A lightweight Matrix session for REST-only admin calls (`/_synapse/admin/*`).
 *
 * Unlike getMatrixSession/ensureClient, this does NOT createClient, startClient,
 * or wait for an initial sync to reach PREPARED — it only exchanges the Cognito
 * JWT for an access token. The chat-admin panel makes plain admin REST calls
 * that need only a token; coupling it to the full chat sync handshake meant any
 * sync hiccup threw before the admin request was ever issued and rendered the
 * whole panel as "Failed to query Synapse: unknown error", while the error-driven
 * remount re-ran the login and leaked a fresh Matrix device on every retry. The
 * JWT login alone (HTTP 200) yields a valid admin token — which is all the admin
 * REST surface requires (verified: whoami + /_synapse/admin/v2/users both 200).
 *
 * Cached per (homeserver, token) so a panel full of admin queries shares ONE
 * login instead of minting a device per request.
 */
export async function getMatrixAdminSession(
  cognitoToken: string,
): Promise<MatrixAdminSession> {
  const key = `${_activeHomeserver}|${tokenTail(cognitoToken)}`
  if (_adminSession && _adminSession.key === key) return _adminSession.promise

  const homeserver = _activeHomeserver
  const promise = (async (): Promise<MatrixAdminSession> => {
    const login = await matrixJwtLogin(homeserver, cognitoToken)
    return { accessToken: login.access_token, userId: login.user_id, homeserver }
  })().catch((err) => {
    // A failed login must not poison the cache — let the next caller retry.
    if (_adminSession?.key === key) _adminSession = null
    throw err
  })

  _adminSession = { key, promise }
  return promise
}

/**
 * Read the current user's own avatar straight from their Matrix profile.
 * This is the source of truth for the avatar (what other users see in chat),
 * so the UI should prefer it over any mirrored copy. Returns the mxc:// URI
 * (or null). The profile endpoint is world-readable, so no token is required.
 */
export async function getMatrixAvatarMxc(
  cognitoToken: string,
): Promise<string | null> {
  const { userId, homeserver } = await getMatrixSession(cognitoToken)
  const enc = encodeURIComponent(userId)
  try {
    const res = await fetch(
      `${homeserver}/_matrix/client/v3/profile/${enc}/avatar_url`,
    )
    if (!res.ok) return null
    const data = (await res.json()) as { avatar_url?: string }
    return data.avatar_url ?? null
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// Mapping helpers
// ---------------------------------------------------------------------------

function matrixPowerToRole(power: number): string {
  if (power >= 100) return 'admin'
  if (power >= 50) return 'support'
  return 'user'
}

function usernameFromMatrixId(matrixUserId: string): string {
  // @cognitoSub:server -> cognitoSub (display fallback)
  return matrixUserId.split(':')[0].replace(/^@/, '')
}

interface MemberLookup {
  username: (matrixUserId: string) => string
  avatarMxc: (matrixUserId: string) => string | null
}

function eventToChatMessage(
  event: MatrixEvent,
  roomId: string,
  lookup?: MemberLookup,
): ChatMessage | null {
  if (event.getType() !== EventType.RoomMessage) return null
  const content = event.getContent() as Record<string, unknown>
  const body = content.body as string | undefined
  if (!body) return null

  const sender = event.getSender() ?? 'unknown'
  const username = lookup ? lookup.username(sender) : usernameFromMatrixId(sender)
  const avatar_url = lookup ? lookup.avatarMxc(sender) : null
  const phenomId = (content.phenom_id as string | undefined) ?? null
  const msgtype = (content.msgtype as string | undefined) ?? MsgType.Text
  const eventType = msgtype === MsgType.Text ? 'text' : msgtype.replace(/^m\./, '')

  return {
    id: event.getId() ?? `${event.getTs()}`,
    room_id: roomId,
    user_id: sender,
    content: body,
    message_type: eventType,
    phenom_id: phenomId,
    is_deleted: event.isRedacted(),
    created_at: new Date(event.getTs()).toISOString(),
    user: {
      id: sender,
      username,
      avatar_url,
    },
  }
}

function memberLookup(room: Room): MemberLookup {
  return {
    username: (matrixUserId: string) =>
      room.getMember(matrixUserId)?.name ?? usernameFromMatrixId(matrixUserId),
    avatarMxc: (matrixUserId: string) => {
      const ev = room.getMember(matrixUserId)?.events?.member
      const content = ev?.getContent() as { avatar_url?: string } | undefined
      return content?.avatar_url ?? null
    },
  }
}

// ---------------------------------------------------------------------------
// Public API: rooms
// ---------------------------------------------------------------------------

export async function fetchRooms(token: string): Promise<ChatRoom[]> {
  const client = await ensureClient(token)
  return client.getRooms().map((r) => {
    const topicEvent = r.currentState.getStateEvents('m.room.topic', '')
    const topic = topicEvent
      ? ((topicEvent.getContent() as { topic?: string }).topic ?? '')
      : ''
    return {
      id: r.roomId,
      name: r.name ?? r.roomId,
      description: topic,
      is_active: true,
    }
  })
}

// ---------------------------------------------------------------------------
// Public API: messages
// ---------------------------------------------------------------------------

/**
 * How many scrollback passes fetchMessages will make before giving up.
 * Each pass asks for SCROLLBACK_BATCH raw events, so the ceiling is
 * MAX_SCROLLBACK_PASSES * SCROLLBACK_BATCH events examined.
 */
const MAX_SCROLLBACK_PASSES = 12
const SCROLLBACK_BATCH = 100

export async function fetchMessages(
  token: string,
  roomId: string,
  limit = 50,
  _before?: string,
): Promise<ChatMessage[]> {
  const client = await ensureClient(token)
  const room = client.getRoom(roomId)
  if (!room) return []

  const timeline = room.getLiveTimeline()

  // Built once per call, not per event: memberLookup walks room state, and the
  // original code hoisted it for that reason.
  const messagesIn = (evs: MatrixEvent[]) => {
    const lookup = memberLookup(room)
    return evs
      .map((e) => eventToChatMessage(e, roomId, lookup))
      .filter((m): m is ChatMessage => m !== null && !m.is_deleted)
  }

  // Paginate until we have `limit` MESSAGES, not until we have `limit` EVENTS.
  //
  // This previously did a single `scrollback(room, limit)` and then filtered to
  // messages. Non-message events -- overwhelmingly m.room.member -- consume that
  // budget, so a room whose recent timeline is membership churn returned ZERO
  // messages and rendered as empty. Measured 2026-08-26: the newest message sat
  // 129 events back in Analysts, 92 in Staff, 65 in Experiencers. Only Phenom Dev
  // (1) and The Red Room (11) fell inside a 50-event window, which is exactly
  // which rooms looked populated.
  //
  // The flooding itself is fixed (phenom-infra #277, #280), but existing history
  // keeps the old events forever, and purging or redacting them is worse than
  // this: purge_history cannot target one event type so it would delete the
  // conversations too, and each redaction is itself a new timeline event.
  //
  // Bounded so a room that genuinely has few messages terminates instead of
  // paginating to the beginning of time on every open.
  let passes = 0
  while (
    messagesIn(timeline.getEvents()).length < limit &&
    passes < MAX_SCROLLBACK_PASSES
  ) {
    const before = timeline.getEvents().length
    await client.scrollback(room, SCROLLBACK_BATCH)
    passes += 1
    // No new events came back: we have reached the start of the room. Stop,
    // otherwise this spins MAX_SCROLLBACK_PASSES times for every short room.
    if (timeline.getEvents().length === before) break
  }

  return messagesIn(timeline.getEvents()).slice(-limit).reverse()
}

export async function sendMessage(
  token: string,
  roomId: string,
  content: string,
  messageType: string = 'text',
  phenomId?: string,
): Promise<ChatMessage> {
  const client = await ensureClient(token)
  const msgtype = messageType === 'text' ? MsgType.Text : (`m.${messageType}` as MsgType)
  // Derive spec-intentional mentions (m.mentions, MSC3952) from the body by
  // matching @DisplayName against room members. The composer inserts mentions
  // as plain text, so deriving here covers both autocomplete picks and
  // hand-typed mentions — and intentional mentions are what Synapse's
  // .m.rule.is_user_mention push rule (mobile push) keys on reliably.
  const mentionedIds = (client.getRoom(roomId)?.getJoinedMembers() ?? [])
    .filter((m) => m.name && !m.name.includes(':') && content.includes(`@${m.name}`))
    .map((m) => m.userId)
  const eventContent = {
    msgtype,
    body: content,
    ...(mentionedIds.length ? { 'm.mentions': { user_ids: mentionedIds } } : {}),
    ...(phenomId ? { phenom_id: phenomId } : {}),
  }
  // matrix-js-sdk TimelineEvents[RoomMessage] requires a discriminated union
  // (body + msgtype + optional relation). Cast through unknown because the
  // phenom_id custom field is not part of the spec union.
  const res = await client.sendEvent(
    roomId,
    EventType.RoomMessage,
    eventContent as unknown as Parameters<typeof client.sendEvent>[2],
  )
  const userId = client.getUserId() ?? 'unknown'
  // Use the resolved displayname for the optimistic stub so the bubble
  // doesn't briefly flash with the raw sub UUID. The room member context
  // already has the sender's displayname after PREPARED state.
  const room = client.getRoom(roomId)
  const member = room?.getMember(userId)
  const memberAvatar = (member?.events?.member?.getContent() as { avatar_url?: string } | undefined)?.avatar_url ?? null
  return {
    id: res.event_id,
    room_id: roomId,
    user_id: userId,
    content,
    message_type: messageType,
    phenom_id: phenomId ?? null,
    is_deleted: false,
    created_at: new Date().toISOString(),
    user: {
      id: userId,
      username: member?.name ?? usernameFromMatrixId(userId),
      avatar_url: memberAvatar,
    },
  }
}

export async function deleteMessage(
  token: string,
  messageId: string,
  roomId: string,
): Promise<void> {
  const client = await ensureClient(token)

  // Redact by room id directly. This used to scan client.getRooms() and call
  // findEventById, which only searches the LOADED timeline -- so redacting an
  // older message threw "not found in any joined room" precisely when the
  // timeline was long, which is exactly when moderation is needed. The server
  // resolves the event; the client does not need it in memory.
  if (!roomId) {
    throw new Error('deleteMessage requires a roomId')
  }
  await client.redactEvent(roomId, messageId)
}

// ---------------------------------------------------------------------------
// Public API: members + moderation
// ---------------------------------------------------------------------------

export async function fetchMembers(
  token: string,
  roomId: string,
): Promise<ChatMember[]> {
  const client = await ensureClient(token)
  const room = client.getRoom(roomId)
  if (!room) return []

  // What this room actually requires to redact someone else's event. Matrix
  // defaults this to 50 when the state event omits it.
  const plEvent = room.currentState
    .getStateEvents('m.room.power_levels', '')
    ?.getContent?.() as { redact?: number } | undefined
  const redactPower = typeof plEvent?.redact === 'number' ? plEvent.redact : 50

  return room.getJoinedMembers().map((m) => {
    const power = room.getMember(m.userId)?.powerLevel ?? 0
    const joinedAt =
      m.events.member?.getTs?.() ??
      new Date().getTime()
    const memberContent = m.events.member?.getContent() as
      | { avatar_url?: string }
      | undefined
    return {
      id: m.userId,
      room_id: roomId,
      user_id: m.userId,
      role: matrixPowerToRole(power),
      power,
      redact_power: redactPower,
      is_muted: false,
      joined_at: new Date(joinedAt).toISOString(),
      user: {
        id: m.userId,
        username: m.name ?? usernameFromMatrixId(m.userId),
        avatar_url: memberContent?.avatar_url ?? null,
      },
    }
  })
}

export async function joinRoom(
  token: string,
  roomId: string,
  fallbackUserId: string,
): Promise<ChatMember> {
  const client = await ensureClient(token)
  await client.joinRoom(roomId)
  const memberId = client.getUserId() ?? fallbackUserId
  return {
    id: memberId,
    room_id: roomId,
    user_id: memberId,
    role: 'user',
    // A freshly joined member has no elevated power. The real values arrive on
    // the next fetchMembers; this object only has to be a valid ChatMember.
    power: 0,
    redact_power: 50,
    is_muted: false,
    joined_at: new Date().toISOString(),
    user: {
      id: memberId,
      username: usernameFromMatrixId(memberId),
    },
  }
}

export async function banUser(
  token: string,
  roomId: string,
  userId: string,
  reason?: string,
): Promise<void> {
  const client = await ensureClient(token)
  await client.ban(roomId, userId, reason)
}

export async function muteUser(
  token: string,
  roomId: string,
  userId: string,
  _durationMinutes?: number,
): Promise<void> {
  // Matrix has no first-class mute. Lower the target user's power level so
  // they can't post. Time-based unmute is a follow-up.
  // setPowerLevel(roomId, userId, powerLevel) - 3 args in matrix-js-sdk v34.
  const client = await ensureClient(token)
  if (!client.getRoom(roomId)) throw new Error(`Room ${roomId} not in joined set`)
  await client.setPowerLevel(roomId, userId, -1)
}

export async function searchUsers(
  token: string,
  query: string,
): Promise<Array<{ id: string; username: string; avatar_url?: string | null }>> {
  const client = await ensureClient(token)
  const res = await client.searchUserDirectory({ term: query, limit: 10 })
  return res.results.map((u) => ({
    id: u.user_id,
    username: u.display_name ?? usernameFromMatrixId(u.user_id),
    avatar_url: u.avatar_url ?? null,
  }))
}

// ---------------------------------------------------------------------------
// Public API: profile (displayname + avatar)
//
// Matrix profile has only two standard fields: displayname and avatar_url.
// avatar_url must be an mxc:// URI; nest stores the same mxc:// in
// users.avatar_url so the UI can serve thumbnails via /_matrix/media/v3/thumbnail.
// ---------------------------------------------------------------------------

/**
 * Server-side full-text chat search (#368). Synapse searches every room the
 * USER is a member of; `roomId` narrows to one room (the in-room search
 * field). Results group per room, newest first, with the event id for
 * jump-to-message deep links (/chats?room=<roomId>&event=<eventId>).
 */
export interface ChatSearchHit {
  eventId: string
  roomId: string
  roomName: string
  sender: string
  body: string
  ts: number
}

export interface ChatSearchRoomGroup {
  roomId: string
  roomName: string
  hits: ChatSearchHit[]
}

export async function searchChatMessages(
  token: string,
  query: string,
  roomId?: string
): Promise<{ groups: ChatSearchRoomGroup[]; total: number }> {
  const client = await ensureClient(token)
  const body = {
    search_categories: {
      room_events: {
        search_term: query,
        keys: ['content.body'],
        order_by: 'recent',
        filter: {
          limit: 50,
          ...(roomId ? { rooms: [roomId] } : {}),
        },
        event_context: { before_limit: 0, after_limit: 0 },
      },
    },
  }
  // matrix-js-sdk's `search()` wraps the same endpoint but munges the
  // grouping; the raw API keeps the response shape predictable.
  const res = (await client.http.authedRequest(
    'POST' as never,
    '/search',
    undefined,
    body
  )) as {
    search_categories?: {
      room_events?: {
        count?: number
        results?: Array<{
          result?: {
            event_id: string
            room_id: string
            sender: string
            origin_server_ts: number
            content?: { body?: string; msgtype?: string }
            type?: string
          }
        }>
      }
    }
  }

  const events = res.search_categories?.room_events
  const byRoom = new Map<string, ChatSearchRoomGroup>()
  for (const r of events?.results ?? []) {
    const ev = r.result
    if (!ev || ev.type !== 'm.room.message' || !ev.content?.body) continue
    const room = client.getRoom(ev.room_id)
    const roomName = room?.name || ev.room_id
    let group = byRoom.get(ev.room_id)
    if (!group) {
      group = { roomId: ev.room_id, roomName, hits: [] }
      byRoom.set(ev.room_id, group)
    }
    group.hits.push({
      eventId: ev.event_id,
      roomId: ev.room_id,
      roomName,
      sender: ev.sender,
      body: ev.content.body,
      ts: ev.origin_server_ts,
    })
  }
  const groups = [...byRoom.values()].sort(
    (a, b) => (b.hits[0]?.ts ?? 0) - (a.hits[0]?.ts ?? 0)
  )
  return { groups, total: events?.count ?? 0 }
}

export async function setDisplayName(
  token: string,
  displayName: string,
): Promise<void> {
  const client = await ensureClient(token)
  await client.setDisplayName(displayName)
}

/**
 * Upload an image to the Synapse media repository.
 * Returns an mxc:// URI suitable for setMatrixAvatar() and storage in
 * Hasura users.avatar_url.
 */
export async function uploadAvatar(
  token: string,
  file: File | Blob,
  filename = 'avatar',
): Promise<string> {
  const client = await ensureClient(token)
  const accessToken = client.getAccessToken()
  if (!accessToken) throw new Error('Matrix access token not available')
  const contentType = (file as File).type || 'application/octet-stream'
  const url = `${_activeHomeserver}/_matrix/media/v3/upload?filename=${encodeURIComponent(filename)}`
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': contentType, Authorization: `Bearer ${accessToken}` },
    body: file,
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Avatar upload failed: ${res.status} ${text.slice(0, 200)}`)
  }
  const data = (await res.json()) as { content_uri?: string }
  if (!data.content_uri) throw new Error('Synapse did not return content_uri')
  return data.content_uri
}

export async function setMatrixAvatar(token: string, mxcUri: string): Promise<void> {
  const client = await ensureClient(token)
  await client.setAvatarUrl(mxcUri)
}

/**
 * Convert mxc:// URI to https thumbnail URL for use in <img src=...>.
 * Returns null for empty / non-mxc inputs so callers can render a fallback.
 * Passes through plain http(s) URLs unchanged (legacy avatar_url values).
 */
export function mxcToHttp(
  mxc: string | null | undefined,
  opts: {
    width?: number
    height?: number
    method?: 'scale' | 'crop'
    /**
     * If true, return the /download URL for the full original media
     * (preserves GIF / animated-WebP animation). Synapse's /thumbnail
     * always returns a static frame, so animated avatars require this.
     * Default false (use /thumbnail) so static avatars still get a
     * cheap thumbnail.
     */
    animated?: boolean
  } = {},
): string | null {
  if (!mxc) return null
  if (mxc.startsWith('http://') || mxc.startsWith('https://')) return mxc
  if (!mxc.startsWith('mxc://')) return null
  const rest = mxc.slice('mxc://'.length)
  const slash = rest.indexOf('/')
  if (slash < 0) return null
  const server = rest.slice(0, slash)
  const mediaId = rest.slice(slash + 1)
  if (opts.animated) {
    return `${_activeHomeserver}/_matrix/media/v3/download/${server}/${mediaId}`
  }
  const w = opts.width ?? 128
  const h = opts.height ?? 128
  // 'crop' default so non-square sources don't distort inside round
  // Avatar slots. Callers can pass method:'scale' for letterboxing.
  const m = opts.method ?? 'crop'
  return `${_activeHomeserver}/_matrix/media/v3/thumbnail/${server}/${mediaId}?width=${w}&height=${h}&method=${m}`
}

// ---------------------------------------------------------------------------
// Public API: real-time subscription
// ---------------------------------------------------------------------------

export function subscribeToMessages(
  token: string,
  roomId: string,
  onMessage: (msg: ChatMessage) => void,
): () => void {
  let cancelled = false
  let detach: (() => void) | null = null

  ensureClient(token)
    .then((client) => {
      if (cancelled) return
      const room = client.getRoom(roomId)
      const lookup = room ? memberLookup(room) : undefined

      const handler = (
        event: MatrixEvent,
        eventRoom: Room | undefined,
        toStartOfTimeline: boolean | undefined,
        _removed: boolean | undefined,
        data: { liveEvent?: boolean } | undefined,
      ) => {
        if (eventRoom?.roomId !== roomId) return
        // Skip scrollback / pagination events — those are loaded via the
        // initial fetchMessages query and would arrive a second time here.
        if (toStartOfTimeline) return
        // Skip events that came from the room's existing state when we
        // attached the listener — they're already in fetchedMessages.
        if (data && data.liveEvent === false) return
        // Skip local echoes (status is non-null while SENDING / NOT_SENT).
        // We let the confirmed version through instead so the sender's
        // displayname is resolved against the up-to-date member context
        // — not the raw sub UUID the optimistic stub carries.
        if (event.status !== null) return
        const msg = eventToChatMessage(event, roomId, lookup)
        if (msg) onMessage(msg)
      }

      client.on(RoomEvent.Timeline, handler)
      detach = () => client.off(RoomEvent.Timeline, handler)
    })
    .catch((err) => {
      // Surface init errors to console; the hook layer falls back gracefully.
      console.error('[chat-subscription] init failed:', err)
    })

  return () => {
    cancelled = true
    if (detach) detach()
  }
}

// ---------------------------------------------------------------------------
// Public API: health check
// ---------------------------------------------------------------------------

export type ConnectionStatus =
  | 'connected'
  | 'auth_error'
  | 'unreachable'
  | 'unknown'

export async function checkConnection(token: string): Promise<ConnectionStatus> {
  try {
    const res = await fetch(`${_activeHomeserver}/_matrix/client/v3/account/whoami`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (res.status === 401 || res.status === 403) return 'auth_error'
    if (!res.ok) return 'unreachable'
    return 'connected'
  } catch {
    return 'unreachable'
  }
}
